import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import {
  useReadAllMutation,
} from "../../features/notificationSlice";
import toast, { Toaster } from "react-hot-toast";
import moment from 'moment';
import { colors } from "../../Shared/colors";


const NotificationModel = ({ notifications, onClose }) => {
  const [readAll, { isLoading: readLoading }] = useReadAllMutation();

  // Helper function to get appropriate icon based on notification type
  const getNotificationIcon = (type, isRead) => {
    if (isRead) return '✓';
    
    switch (type) {
      case 'Assigned Leads':
        return '👤';
      case 'Payment':
        return '💰';
      case 'Meeting':
        return '📅';
      case 'Task':
        return '✅';
      case 'Policy':
        return '📋';
      case 'Facebook Lead':
        return '📘';
      case 'Feedback':
        return '💬';
      case 'Client Search':
        return '🔍';
      default:
        return '🔔';
    }
  };

  // Helper function to get color based on notification type
  const getTypeColor = (type) => {
    switch (type) {
      case 'Assigned Leads':
        return '#3b82f6'; // Blue
      case 'Payment':
        return '#10b981'; // Green
      case 'Meeting':
        return '#f59e0b'; // Amber
      case 'Task':
        return '#8b5cf6'; // Purple
      case 'Policy':
        return '#ef4444'; // Red
      case 'Facebook Lead':
        return '#1877f2'; // Facebook Blue
      case 'Feedback':
        return '#06b6d4'; // Cyan
      case 'Client Search':
        return '#f97316'; // Orange
      default:
        return colors.brown;
    }
  };

  const markAllAsRead = () => {
    readAll()
      .unwrap()
      .then((res) => {
        toast.success("Marked all as read!");
      })
      .catch((err) => {
        toast.error("Unable to mark all notifications as read!");
      });
  };

  const unreadCount = notifications?.filter(notification => !notification.isRead).length || 0;

  return (
    <ModalOverlay>
    <NotificationContainer>
        <HeaderSection>
          <HeaderContent>
            <HeaderIcon>🔔</HeaderIcon>
            <HeaderTitle>Notifications</HeaderTitle>
            {unreadCount > 0 && (
              <UnreadCount>{unreadCount} unread</UnreadCount>
            )}
          </HeaderContent>
          <HeaderActions>
            <ActionButton onClick={markAllAsRead} disabled={readLoading}>
              {readLoading ? (
                <>
                  <LoadingSpinner />
                  Processing...
                </>
              ) : (
                <>
                  ✓ Mark All as Read
                </>
              )}
            </ActionButton>
            <CloseButton onClick={onClose}>
              ✕
            </CloseButton>
          </HeaderActions>
        </HeaderSection>
        
        <NotificationList>
          {notifications && notifications.length > 0 ? (
          notifications.map((notification, index) => (
              <NotificationItem key={notification._id || index} isRead={notification?.isRead} type={notification?.type}>
                <NotificationContent>
                  <NotificationIcon isRead={notification?.isRead} type={notification?.type}>
                    {getNotificationIcon(notification?.type, notification?.isRead)}
                  </NotificationIcon>
                  <NotificationText>
                    <NotificationType>{notification?.type || 'Notification'}</NotificationType>
                    <Message>{notification?.message}</Message>
                    <Timestamp>{moment(new Date(notification?.createdAt)).fromNow(true)} ago</Timestamp>
                  </NotificationText>
                </NotificationContent>
                {!notification?.isRead && <UnreadIndicator type={notification?.type} />}
              </NotificationItem>
          ))
        ) : (
            <EmptyState>
              <EmptyIcon>📭</EmptyIcon>
              <EmptyTitle>No New Notifications!</EmptyTitle>
              <EmptySubtitle>You're all caught up. New notifications will appear here.</EmptySubtitle>
            </EmptyState>
          )}
        </NotificationList>
    </NotificationContainer>
    </ModalOverlay>
  );
};

export default NotificationModel;

// Styled Components

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NotificationContainer = styled.div`
  position: relative;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  background: ${colors.white};
  border-radius: 20px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${colors.brown}, ${colors.textBlue});
    z-index: 1;
  }
`;

const HeaderSection = styled.div`
  background: linear-gradient(135deg, ${colors.lightWhite} 0%, ${colors.white} 100%);
  padding: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const HeaderIcon = styled.div`
  font-size: 2rem;
  color: ${colors.brown};
`;

const HeaderTitle = styled.h2`
  color: ${colors.text};
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
`;

const UnreadCount = styled.div`
  background: ${colors.brown};
  color: ${colors.white};
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-left: 1rem;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  background: linear-gradient(135deg, ${colors.brown} 0%, #1a6b6b 100%);
  color: ${colors.white};
  box-shadow: 0 4px 12px rgba(36, 147, 147, 0.3);
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CloseButton = styled.button`
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: ${colors.white};
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
  
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid ${colors.white};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const NotificationList = styled.div`
  max-height: 50vh;
  overflow-y: auto;
  padding: 1rem;

   &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${colors.lightWhite};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.brown};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #1a6b6b;
  }
`;

const NotificationItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  margin-bottom: 0.75rem;
  background: ${props => props.isRead ? colors.lightWhite : colors.white};
  border: 1px solid ${props => props.isRead ? colors.inputBorderWhite : getTypeColor(props.type)};
  border-radius: 12px;
  position: relative;
  cursor: pointer;

  
  ${props => !props.isRead && `
    border-left: 4px solid ${getTypeColor(props.type)};
    background: linear-gradient(135deg, ${colors.white} 0%, ${colors.lightWhite} 100%);
  `}
`;

const NotificationContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex: 1;
`;

const NotificationIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.isRead ? colors.lightWhite : getTypeColor(props.type)};
  color: ${props => props.isRead ? colors.textMed : colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
  border: 2px solid ${props => props.isRead ? colors.inputBorderWhite : getTypeColor(props.type)};

`;

const NotificationText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const NotificationType = styled.div`
  color: ${colors.textMed};
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
`;

const Message = styled.div`
  color: ${colors.text};
  font-size: 0.95rem;
  font-weight: 500;
  line-height: 1.4;
  margin-bottom: 0.25rem;
`;

const Timestamp = styled.div`
  color: ${colors.textMed};
  font-size: 0.8rem;
  font-weight: 400;
`;

const UnreadIndicator = styled.div`
  width: 8px;
  height: 8px;
  background: ${props => getTypeColor(props.type) || colors.brown};
  border-radius: 50%;
  flex-shrink: 0;
  margin-left: 1rem;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const EmptyTitle = styled.h3`
  color: ${colors.text};
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
`;

const EmptySubtitle = styled.p`
  color: ${colors.textMed};
  font-size: 0.95rem;
  margin: 0;
  max-width: 300px;
`;

// Helper function to get color based on notification type
const getTypeColor = (type) => {
  switch (type) {
    case 'Assigned Leads':
      return '#3b82f6'; // Blue
    case 'Payment':
      return '#10b981'; // Green
    case 'Meeting':
      return '#f59e0b'; // Amber
    case 'Task':
      return '#8b5cf6'; // Purple
    case 'Policy':
      return '#ef4444'; // Red
    case 'Facebook Lead':
      return '#1877f2'; // Facebook Blue
    case 'Feedback':
      return '#06b6d4'; // Cyan
    default:
      return colors.brown;
  }
};
